// hooks/market/useUpdateShop.ts
//
// Gère les actions de modification ET de suppression d'une boutique
// (distinct de useMyShops.updateUiConfig, dédié à ui_config).
//
// - updateShop(shopId, fields) : met à jour un ou plusieurs champs d'une
//   boutique. Utilisé notamment après un upload d'image via
//   ImagePickerField, pour enregistrer cover_image_url sur la boutique.
// - deleteShop(shopId) : supprime définitivement une boutique (et, par
//   cascade DB, tous ses produits — voir market_products_shop_id_fkey
//   ON DELETE CASCADE). Action irréversible.
//
// Les deux actions ont leurs propres états de chargement/erreur
// indépendants (saving/error vs deleting/deleteError), pour que l'UI
// puisse distinguer "en train d'enregistrer" de "en train de supprimer"
// sans ambiguïté.
//
// Appelle le vrai backend :
//   POST {API_BASE_URL}/market   action: 'update-shop', { shop_id, ...fields }
//   POST {API_BASE_URL}/market   action: 'delete-shop', { shop_id }
// (voir backend/handleMarket.ts → updateShop / deleteShop)
//
// ShopUpdateFields reprend EXACTEMENT la liste blanche ALLOWED_FIELDS du
// handler updateShop (patch V9) — tous les champs modifiables par le
// owner d'une boutique via cette action. Volontairement absents (voir
// handleMarket.ts pour le détail) :
//   - slug              → verrouillé après création (SEO / liens partagés)
//   - disabled_reason    → réservé à l'admin
//   - owner_id, is_verified, is_premium, owner_is_active,
//     published_at, disabled_at, archived_at → colonnes système/trigger
//   - ui_config          → géré séparément par useMyShops.updateUiConfig
//     (action 'update-shop-ui-config', objet complet requis)
//
// Tous les champs sont optionnels ici : l'appelant n'envoie que ce qu'il
// veut réellement modifier (le backend ignore les clés absentes du body).

import { useState, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';

export type ShopStatus = 'draft' | 'published' | 'disabled' | 'archived';

export interface ShopUpdateFields {
  // Identité / présentation
  name?: string;
  tagline?: string;
  description?: string;
  cover_image_url?: string | null;

  // Contact & boutons d'action
  whatsapp_business_phone?: string | null;
  show_whatsapp_business_button?: boolean;
  show_platform_chat_button?: boolean;
  contact_email?: string | null;
  contact_phone?: string | null;

  // Localisation
  country?: string | null;
  city?: string | null;
  address?: string | null;
  currency_code?: string;
  language_code?: string;
  timezone?: string;

  // SEO / partage social
  seo_title?: string | null;
  seo_description?: string | null;
  og_image_url?: string | null;

  // Statut de publication
  status?: ShopStatus;
}

export function useUpdateShop() {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const [deleting, setDeleting]       = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const updateShop = useCallback(async (
    shopId: string,
    fields: ShopUpdateFields
  ): Promise<boolean> => {
    setSaving(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return false;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'update-shop',
          access_token: token,
          shop_id:      shopId,
          ...fields,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de mettre à jour la boutique');
        return false;
      }

      return true;
    } catch (err) {
      console.error('[useUpdateShop][updateShop]', err);
      setError('network_error');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Suppression définitive d'une boutique (owner only, côté backend). Par
  // cascade DB (market_products_shop_id_fkey ON DELETE CASCADE), tous les
  // produits de la boutique sont supprimés en même temps — irréversible,
  // à confirmer explicitement côté UI avant d'appeler cette fonction.
  const deleteShop = useCallback(async (
    shopId: string
  ): Promise<boolean> => {
    setDeleting(true);
    setDeleteError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setDeleteError('session_expired');
        return false;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'delete-shop',
          access_token: token,
          shop_id:      shopId,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setDeleteError(data.error || 'Impossible de supprimer la boutique');
        return false;
      }

      return true;
    } catch (err) {
      console.error('[useUpdateShop][deleteShop]', err);
      setDeleteError('network_error');
      return false;
    } finally {
      setDeleting(false);
    }
  }, []);

  return {
    updateShop, saving, error,
    deleteShop, deleting, deleteError,
  };
}
